import angular from 'angular';
import { configureStates, resolveFromParam, RouteDeclaration } from '../../core/router/router-helper.provider';
import { RouterModule } from '../../core/router/router.module';

const identityAreaPrefix = "Identity/Account";
const coreIdentityPaths = {
    AccessDenied: null, // Handled independently
    ConfirmEmail: "confirmEmail",
    ConfirmEmailChange: "confirmEmailChange",
    ExternalLogin: "externalLogin",
    ForgotPassword: "forgotPassword",
    ForgotPasswordConfirmation: "forgotPasswordConfirmation",
    Lockout: "lockout",
    Login: "login",
    LoginWith2fa: "loginWith2fa",
    LoginWithRecoveryCode: "loginWithRecoveryCode", // Not implemented
    Logout: "logout",
    // Registration is disabled, so the routes should 404
    Register: null,
    RegisterConfirmation: null,
    ResendEmailConfirmation: "resendEmailConfirmation",
    ResetPassword: "resetPassword",
    ResetPasswordConfirmation: "resetPasswordConfirmation",
    SendCode: "sendCode",
    "Manage/ChangePassword": "manage.changePassword",
    // Personal data for Apex cannot be managed by the user.
    "Manage/DeletePersonalData": null,
    "Manage/Disable2fa": "manage.disable2fa",
    "Manage/DownloadPersonalData": "manage.downloadPersonalData",
    "Manage/Email": "manage.email",
    "Manage/EnableAuthenticator": "manage.enableAuthenticator",
    "Manage/EnableSms2fa": "manage.enableSms2fa",
    "Manage/ExternalLogins": "manage.externalLogins",
    "Manage/GenerateRecoveryCodes": "manage.generateRecoveryCodes",
    "Manage/Index": "manage.profile",
    "Manage/PersonalData": null,
    "Manage/ResetAuthenticator": "manage.resetAuthenticator",
    "Manage/SetPassword": "manage.setPassword",
    "Manage/ShowRecoveryCodes": "manage.showRecoveryCodes",
    "Manage/TwoFactorAuthentication": "manage.twoFactorAuthentication",
    "Manage/VerifyPhoneNumber": "manage.verifyPhoneNumber"
};

// Various server-rendered pages use query parameters. Accept these values on any route since
// they aren't needed for any client-side logic. Provide both camel and pascal case since
// MVC uses both inconsistently, but ui-router is case-sensitive.
const queryParams = [
    "returnUrl",
    "ReturnUrl",
    "userId",
    "UserId",
    "email",
    "Email",
    "code",
    "Code",
    "rememberMe",
    "RememberMe",
    "provider",
    "Provider"
];
const queryParamsMatch = queryParams.join("&");

const coreIdentityRoutes: RouteDeclaration[] = Object.keys(coreIdentityPaths)
    .filter(path => !!coreIdentityPaths[path])
    .map(path => ({
        state: `account.${coreIdentityPaths[path]}`,
        config: {
            url: `/${path}?${queryParamsMatch}`,
            params: {
                requireReload: true
            },
            data: {
                isMvc: true,
                allowAnonymous: !path.startsWith("Manage/"),
                skipAuthGuard: true
            }
        }
    }));


// Set up aliases to avoid breaking existing URLs. TODO: Fix any hard-coded paths
// that don't use ui-sref to generate the routing links.
const frameworkIdentityAliases = {
    "Account/Login": "login",
    "Account/AccessDenied": "accessDenied",
    "Account/ForgotPassword": "forgotPassword",
    "Account/ForgotPasswordConfirmation": "forgotPasswordConfirmation",
    "Account/ResetPassword": "resetPassword",
    "Account/ResetPasswordConfirmation": "resetPasswordConfirmation",
    "Account/SendCode": "sendCode",
    "Account/VerifyCode": "loginWith2fa",
    "Account/ExternalLoginFailure": "externalLoginFailure",
    "Manage/ChangePassword": "manage.changePassword",
    "Manage/AddPhoneNumber": "manage.twoFactorAuthentication",
    "Manage/VerifyPhoneNumber": "manage.verifyPhoneNumber",
}

const frameworkIdentityAliasRoutes: RouteDeclaration[] = Object.keys(frameworkIdentityAliases)
    .map(path => ({
        state: `account.${frameworkIdentityAliases[path]}.alias`,
        config: {
            url: `^/${path}?${queryParamsMatch}`,
            redirectTo: `account.${frameworkIdentityAliases[path]}`,
        }
    }));

const routes: RouteDeclaration[] = [
    {
        state: "identityServerError",
        config: {
            name: "identityServerError",
            url: `/identity/error`,
            params: {
                requireReload: true
            },
            data: {
                isMvc: true,
                allowAnonymous: true,
                skipAuthGuard: true
            }
        }
    },
    {
        state: "account",
        config: {
            url: `/${identityAreaPrefix}`,
            abstract: true
        }
    },
    {
        state: "account.accessDenied",
        config: {
            url: "/accessdenied",
            redirectTo: "403"
        }
    },
    {
        state: "account.sessionExpired",
        config: {
            url: "/session-expired",
            params: {
                returnUrl: { type: "string", value: null }
            },
            data: {
                allowAnonymous: true
            },
            resolve: {
                returnUrl: resolveFromParam("returnUrl")
            },
            component: "apexSessionExpired"
        }
    },
    ...coreIdentityRoutes,
    {
        state: "account.manage",
        config: {
            url: "",
            abstract: true
        }
    },
    {
        state: "account.manage.index",
        config: {
            url: "/Manage",
            redirectTo: "account.manage.profile",
            data: {
                skipAuthGuard: true
            }
        }
    },
    ...frameworkIdentityAliasRoutes,
    {
        state: "account.management",
        config: {
            url: "^/account/manage?message&validTil",
            component: "apexManageAccountSettingsContainer",
            resolve: {
                toast: $stateParams => {
                    'ngInject';
                    if ($stateParams.message) {
                        return $stateParams.message.toLowerCase();
                    }
                    return null;
                },
                validTil: $stateParams => {
                    'ngInject'
                    return $stateParams.validTil;
                }
            },
            data: {
                pageHeader: "Manage your account settings",
                ignoreRole: true,
                skipAuthGuard: true
            }
        }
    },
    {
        state: "account.management.index",
        config: {
            url: "/index",
            redirectTo: "account.management"
        }
    }];

export const AccountRoutingModule = angular
    .module("apex.mainApp.account.routing", [
        RouterModule
    ])
    .run(configureStates(routes))
    .name;
